// Tenant WebSocket Service for Real-time Tenant-level Notifications
import config from '../config/config';
import { authService } from './authService';
import { getOptimalWebSocketConfig } from '../utils/browserUtils';
import { performanceMonitor } from '../utils/performanceMonitor';
import { normalizeInternalConversationNewMessage } from './internalChatService';

class TenantWebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    
    // Get browser-specific configuration
    const wsConfig = getOptimalWebSocketConfig();
    this.maxReconnectAttempts = wsConfig.maxReconnectAttempts;
    this.reconnectInterval = wsConfig.reconnectInterval;
    this.heartbeatTimeout = wsConfig.heartbeatTimeout;
    
    this.heartbeatInterval = null;
    this.isConnecting = false;
    this.isReconnecting = false;
    this.listeners = new Map();
    this.messageQueue = [];
    
    // Store connection parameters for reconnection
    this.currentTenantId = null;
    this.currentUserId = null;
  }

  // Connect to Tenant WebSocket
  async connect(tenantId, userId) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      ////console.log('🏢 Tenant WebSocket already connected or connecting');
      return;
    }

    // Store connection parameters for reconnection
    this.currentTenantId = tenantId;
    this.currentUserId = userId;

    this.isConnecting = true;

    if (!tenantId) {
      console.error('❌ No tenant ID provided for Tenant WebSocket connection');
      this.isConnecting = false;
      return;
    }

    if (!userId) {
      console.error('❌ No user ID provided for Tenant WebSocket connection');
      this.isConnecting = false;
      return;
    }

    try {
      // Get and validate token before connecting
      const token = await this.getValidToken();
      if (!token) {
        console.error('❌ No valid access token available for Tenant WebSocket connection');
        this.isConnecting = false;
        return;
      }

      // Build Tenant WebSocket URL with authentication
      console.log('config.API.Websocket_tenant_Base_URL', config.API.Websocket_tenant_Base_URL);

      const wsUrl = `${config.API.Websocket_tenant_Base_URL}${tenantId}/queue/?token=${token}`;
      //console.log('🏢 Connecting to Tenant WebSocket:', wsUrl);
      
      this.socket = new WebSocket(wsUrl);
      this.setupEventListeners();
    } catch (error) {
      console.error('❌ Tenant WebSocket connection error:', error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  // Get valid token, refresh if necessary
  async getValidToken() {
    try {
      const currentToken = localStorage.getItem('access_token');
      
      if (!currentToken) {
        console.warn('⚠️ No access token found, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping Tenant WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }

      // Verify token is still valid
      const verificationResult = await authService.verifyToken(currentToken);
      if (verificationResult.isValid) {
        return currentToken;
      } else {
        console.warn('⚠️ Access token is invalid, attempting to refresh...');
        const refreshResult = await authService.refreshAccessToken();
        if (refreshResult.success) {
          return refreshResult.accessToken;
        } else {
          console.error('❌ Failed to refresh token:', refreshResult.error);
          // If logout is required, don't attempt reconnection
          if (refreshResult.shouldLogout) {
            console.warn('🔐 Logout required - stopping Tenant WebSocket reconnection attempts');
            this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          }
          return null;
        }
      }
    } catch (error) {
      console.error('❌ Error getting valid token:', error);
      return null;
    }
  }

  // Handle token expiration during WebSocket connection
  async handleTokenExpiration() {
    //console.log('🔄 Handling token expiration for Tenant WebSocket, attempting to refresh...');
    
    try {
      const refreshResult = await authService.refreshAccessToken();
      if (refreshResult.success) {
        //console.log('✅ Token refreshed successfully, reconnecting Tenant WebSocket...');
        // Disconnect current connection and reconnect with new token
        this.disconnect();
        // Small delay to ensure clean disconnection
        setTimeout(() => {
          if (this.currentTenantId && this.currentUserId) {
            this.connect(this.currentTenantId, this.currentUserId);
          }
        }, 1000);
        return true;
      } else {
        console.error('❌ Token refresh failed:', refreshResult.error);
        // If logout is required, don't attempt reconnection - logout will handle cleanup
        if (refreshResult.shouldLogout) {
          console.warn('🔐 Logout required - stopping Tenant WebSocket reconnection attempts');
          this.emit('token_refresh_failed', { error: refreshResult.error, shouldLogout: true });
          // Disconnect cleanly since logout is in progress
          this.disconnect();
          return false;
        }
        this.emit('token_refresh_failed', { error: refreshResult.error, retryable: refreshResult.retryable });
        return false;
      }
    } catch (error) {
      console.error('❌ Error during token refresh:', error);
      this.emit('token_refresh_failed', { error: error.message });
      return false;
    }
  }

  // Setup WebSocket event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      //console.log('✅ Tenant WebSocket connected successfully');
      this.isConnecting = false;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.processMessageQueue();
      this.emit('connected', event);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        //console.log('📨 Tenant WebSocket message received:', data);
        this.handleMessage(data);
      } catch (error) {
        console.error('❌ Error parsing Tenant WebSocket message:', error);
      }
    };

    this.socket.onclose = async (event) => {
      //console.log('❌ Tenant WebSocket connection closed:', event.code, event.reason);
      this.isConnecting = false;
      this.stopHeartbeat();
      
      // Check if closure was due to authentication failure (401)
      if (event.code === 1008 || event.code === 1002 || event.reason?.includes('401') || event.reason?.includes('unauthorized')) {
        //console.log('🔑 Tenant WebSocket closed due to authentication failure, attempting token refresh...');
        const refreshSuccess = await this.handleTokenExpiration();
        if (refreshSuccess) {
          return; // Token refresh will trigger reconnection
        }
      }
      
      // Only attempt reconnection if it wasn't a manual close
      if (event.code !== 1000) {
        this.isReconnecting = true;
        this.emit('connecting', { reason: 'reconnecting' });
        this.handleReconnect();
      } else {
        this.isReconnecting = false;
        this.emit('disconnected', event);
      }
    };

    this.socket.onerror = (error) => {
      console.error('❌ Tenant WebSocket error:', error);
      this.isConnecting = false;
      
      if (!this.isReconnecting) {
        this.emit('error', error);
      } else {
        //console.log('🔄 Error during reconnection - not emitting error event to avoid status change');
      }
    };
  }

  // Handle incoming tenant-level messages
  handleMessage(data) {
    //console.log('📨 Raw WebSocket message received:', data);
    
    // Handle pong responses to ping messages
    if (data.type === 'pong' || data.event === 'pong') {
      //console.log('🏓 Received pong response, connection is alive');
      if (this.lastPingTimeout) {
        clearTimeout(this.lastPingTimeout);
        this.lastPingTimeout = null;
      }
      return;
    }

    // Envelope: { type: "Internal.Notification", payload: { event, conversation } }
    // Without unwrapping, the switch would see type Internal.Notification and never route internal.* events.
    const notifType = (data?.type ?? '').toString();
    const inner = data.payload && typeof data.payload === 'object' ? data.payload : null;
    const innerEvent = inner?.event != null ? String(inner.event) : '';
    const unwrapInternalFavorite =
      innerEvent === 'internal.conversation.favorite.add' ||
      innerEvent === 'internal.conversation.favorite.remove';
    if (
      notifType.toLowerCase() === 'internal.notification' &&
      inner &&
      inner.event &&
      (inner.conversation || inner.message || inner.conversation_id != null || unwrapInternalFavorite)
    ) {
      this.handleMessage({ ...inner });
      return;
    }
    
    // Handle different message formats
    let type, payload;
    
    if (data.event && data.conversation) {
      // Format: { event: "conversation.accepted", conversation: {...} }
      type = data.event;
      payload = data.conversation;
      //console.log('🔍 Detected event format: event + conversation');
    } else if (data.type && data.payload) {
      // Format: { type: "chat_accepted", payload: {...} }
      type = data.type;
      payload = data.payload;
      //console.log('🔍 Detected event format: type + payload');
    } else if (data.event) {
      // Event-only envelope: { event: "message.new.spam", ... } (no conversation/payload)
      type = data.event;
      payload = data;
    } else {
      // Fallback: treat entire data as payload
      type = data.type || 'unknown';
      payload = data;
      //console.log('🔍 Using fallback format');
    }
    
    //console.log('📨 Processed message - Type:', type, 'Payload:', payload);
    
    if (type === 'unknown' && typeof data?.event === 'string' && data.event.startsWith('internal.')) {
      type = data.event;
      payload = data;
    }

    switch (type) {
      case 'chat_status_changed':
        //console.log('📊 Emitting chat_status_changed event');
        this.emit('chat_status_changed', payload);
        break;
      case 'chat_accepted':
        //console.log('✅ Emitting chat_accepted event');
        this.emit('chat_accepted', payload);
        break;
      case 'chat_resolved':
        //console.log('✅ Emitting chat_resolved event');
        this.emit('chat_resolved', payload);
        break;
      case 'chat_alert':
        //console.log('🚨 Emitting chat_alert event');
        this.emit('chat_alert', payload);
        break;
      case 'conversation.accepted':
        // Handle the new conversation.accepted event
        //console.log('✅ Processing conversation.accepted event:', payload);
        //console.log('✅ Emitting conversation_accepted event to listeners');
        this.emit('conversation_accepted', payload);
        break;
      case 'conversation.moved_to_alerts':
        this.emit('conversation_moved_to_alerts', {
          conversation: payload,
          reason: data.reason ?? null,
        });
        break;
      case 'conversation.updated':
        // Handle the new conversation.updated event
        //console.log('🔄 Processing conversation.updated event:', payload);
        
        // TENANT WEBSOCKET: Only emit events that are relevant to tenant scope (All/Alerts tabs)
        // Filter out user-scope events to prevent duplicate processing
        const status = payload?.status;
        
        // Check if this is a tenant-scope event
        const allTabStatuses = ['New', 'Bot Handling', 'Resolved', 'Reopened', 'NEW', 'BOT_HANDLING', 'RESOLVED', 'REOPENED'];
        const alertsStatuses = ['AWAITING_AGENT', 'Awaiting Agent', 'awaiting_agent', 'Awaiting_Agent'];
        const isTenantScopeEvent = allTabStatuses.includes(status) || alertsStatuses.includes(status);
        
        if (isTenantScopeEvent) {
          //console.log('🔄 Emitting conversation_updated event (tenant scope)');
          this.emit('conversation_updated', payload);
        } else {
          //console.log('⏭️ Skipping conversation_updated event (user scope - handled by user websocket)');
        }
        break;
      case 'message.status.updated':
        // Handle message status updates (sent, delivered, read)
        //console.log('📊 Processing message.status.updated event:', payload);
        this.emit('message_status_updated', payload);
        break;
      // ===== Spam events ('Mark Chat as Spam' story) =====
      // Emit the full original envelope so listeners can read both the conversation and message.
      case 'message.new.spam':
        this.emit('message.new.spam', data);
        break;
      // ===== Follow-ups: { event: "followup.updated", action, follow_up } =====
      case 'followup.updated':
        this.emit('followup.updated', data.follow_up || data.followUp ? data : payload);
        break;
      case 'conversation.marked_spam':
        this.emit('conversation.marked_spam', data);
        break;
      case 'conversation.removed_from_spam':
        this.emit('conversation.removed_from_spam', data);
        break;
      // ===== Bot Auto-Resolve Review Card ('Auto-Chat Resolved by Bot' story) =====
      // Emit the full original envelope (card_id, conversation_id, customer_name, expires_at, messages, ...).
      case 'conversation.bot_auto_resolve_card':
        this.emit('conversation.bot_auto_resolve_card', data);
        break;
      case 'conversation.bot_auto_resolve_card_dismissed':
        this.emit('conversation.bot_auto_resolve_card_dismissed', data);
        break;
      // ===== Alerted Conversation Alarm cards ('Alerted Conversation Alarm' story) =====
      // Emit the full original envelope (cards[], expires_at, ring_seconds, ...).
      case 'conversation.alerts_alarm_card':
        this.emit('conversation.alerts_alarm_card', data);
        break;
      case 'conversation.alerts_alarm_card_dismissed':
        this.emit('conversation.alerts_alarm_card_dismissed', data);
        break;
      case 'conversation.alerts_alarm_card_item_removed':
        this.emit('conversation.alerts_alarm_card_item_removed', data);
        break;
      case 'user_online':
        //console.log('🟢 Emitting user_online event');
        this.emit('user_online', payload);
        break;
      case 'user_offline':
        //console.log('🔴 Emitting user_offline event');
        this.emit('user_offline', payload);
        break;
      case 'tenant_notification':
        //console.log('📢 Emitting tenant_notification event');
        this.emit('tenant_notification', payload);
        break;
      case 'system_announcement':
        //console.log('📢 Emitting system_announcement event');
        this.emit('system_announcement', payload);
        break;
      case 'internal.conversation.group.add':
        // New internal group conversation — user was added to a group
        this.emit('internal_conversation_group_add', payload);
        break;
      case 'internal.conversation.direct.add':
        this.emit('internal_conversation_direct_add', payload);
        break;
      case 'internal.conversation.add':
        this.emit('internal_conversation_add', payload);
        break;
      case 'internal.conversation.new.message': {
        const normalized = normalizeInternalConversationNewMessage(
          data,
          data.event && data.conversation ? payload : null
        );
        if (normalized) {
          this.emit('internal_conversation_new_message', normalized);
        }
        break;
      }
      
      case 'internal.conversation.group.subject.changed':
      case 'internal.conversation.group.subject.change':
        this.emit('internal_conversation_group_subject_changed', payload);
        break;
      case 'internal.conversation.group.remove':
        this.emit('internal_conversation_group_remove', payload);
        break;
      case 'internal.conversation.group.owner.transfer':
        this.emit('internal_conversation_group_owner_transfer', payload);
        break;
      case 'internal.conversation.favorite.add':
        this.emit('internal_conversation_favorite_add', payload);
        break;
      case 'internal.conversation.favorite.remove':
        this.emit('internal_conversation_favorite_remove', payload);
        break;
      case 'pong':
        // Heartbeat response
        //console.log('💓 Heartbeat response received');
        break;
      default:
        //console.log('❓ Unknown tenant message type:', type, 'Data:', data);
        this.emit('unknown', data);
    }
  }

  // Send tenant-level message through WebSocket
  sendMessage(messageData) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'tenant_message',
        payload: messageData
      };
      
      // Sending Tenant WebSocket message
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ Tenant WebSocket not connected, queuing message');
      this.messageQueue.push(messageData);
    }
  }

  // Send chat status update
  sendChatStatusUpdate(chatId, status, userId) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'chat_status_update',
        payload: {
          chat_id: chatId,
          status: status,
          user_id: userId,
          timestamp: new Date().toISOString()
        }
      };
      
      // Sending chat status update
      this.socket.send(JSON.stringify(message));
    }
  }

  // Send chat acceptance notification
  sendChatAccepted(chatId, userId, userName) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'chat_accepted',
        payload: {
          chat_id: chatId,
          accepted_by: userId,
          accepted_by_name: userName,
          timestamp: new Date().toISOString()
        }
      };
      
      // Sending chat accepted notification
      this.socket.send(JSON.stringify(message));
    }
  }


  // Start heartbeat to keep connection alive and detect stale connections
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send ping and set up response timeout
        const pingId = Date.now();
        this.socket.send(JSON.stringify({ type: 'ping', ts: pingId }));
        
        // Set up response timeout - if no pong received within 30 seconds, consider connection stale
        const responseTimeout = setTimeout(() => {
          console.warn('⚠️ Tenant WebSocket ping timeout - connection may be stale, attempting reconnection...');
          this.handleStaleConnection();
        }, 30000);
        
        // Store timeout for cleanup when pong is received
        this.lastPingTimeout = responseTimeout;
      }
    }, this.heartbeatTimeout);
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.lastPingTimeout) {
      clearTimeout(this.lastPingTimeout);
      this.lastPingTimeout = null;
    }
  }

  // Handle stale connection detection
  handleStaleConnection() {
    console.warn('🔄 Detected stale Tenant WebSocket connection, forcing reconnection...');
    this.isReconnecting = true;
    this.emit('connecting', { reason: 'stale_connection' });
    
    // Force close the current connection
    if (this.socket) {
      this.socket.close(1000, 'Stale connection detected');
    }
    
    // Attempt reconnection
    this.handleReconnect();
  }

  // Process queued messages
  processMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }

  // Handle reconnection
  async handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached for Tenant WebSocket - stopping reconnection attempts');
      this.isReconnecting = false;
      this.emit('disconnected', { reason: 'max_attempts_reached' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    // Check if we have stored connection parameters
    if (!this.currentTenantId || !this.currentUserId) {
      console.error('❌ Cannot reconnect Tenant WebSocket: missing tenant ID or user ID');
      this.isReconnecting = false;
      this.emit('disconnected', { reason: 'missing_parameters' });
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    this.reconnectAttempts++;
    //console.log(`🔄 Tenant WebSocket reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} - retrying in ${this.reconnectInterval}ms`);
    //console.log(`🔄 Reconnecting with tenantId: ${this.currentTenantId}, userId: ${this.currentUserId}`);
    
    if (this.reconnectAttempts === 1) {
      this.emit('connecting', { 
        reason: 'reconnecting', 
        attempt: this.reconnectAttempts, 
        maxAttempts: this.maxReconnectAttempts 
      });
    }
    
    // Exponential backoff: 3s, 6s, 12s, 24s, 30s (max)
    const backoffDelay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    setTimeout(async () => {
      if (this.socket && this.socket.readyState === WebSocket.CLOSED) {
        try {
          const token = await this.getValidToken();
          if (token) {
            await this.connect(this.currentTenantId, this.currentUserId);
          } else {
            // Check if we should stop reconnection attempts (logout in progress)
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              // No refresh token means logout is in progress - stop reconnection attempts
              console.warn('🔐 No refresh token available - logout in progress, stopping Tenant WebSocket reconnection attempts');
              this.isReconnecting = false;
              this.emit('disconnected', { reason: 'logout_in_progress' });
              return;
            }
            console.error('❌ Cannot reconnect Tenant WebSocket: no valid token available');
            this.emit('token_refresh_failed', { error: 'No valid token available for reconnection' });
          }
        } catch (error) {
          console.error('❌ Error during Tenant WebSocket reconnection:', error);
          this.handleReconnect();
        }
      }
    }, backoffDelay);
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emit event to listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Error in Tenant WebSocket event listener:', error);
        }
      });
    }
  }

  // Disconnect Tenant WebSocket
  disconnect() {
    //console.log('🔌 Disconnecting Tenant WebSocket');
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    
    this.isConnecting = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.messageQueue = [];
    
    // Clear stored connection parameters
    this.currentTenantId = null;
    this.currentUserId = null;
  }

  // Get connection status
  getConnectionStatus() {
    if (!this.socket) return 'disconnected';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  // Check if connected
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  // Update connection parameters (useful when switching tenants)
  updateConnectionParams(tenantId, userId) {
    //console.log('🔄 Updating Tenant WebSocket connection parameters:', { tenantId, userId });
    
    // If we're currently connected to a different tenant, disconnect first
    if (this.isConnected() && (this.currentTenantId !== tenantId || this.currentUserId !== userId)) {
      //console.log('🔄 Switching tenants, disconnecting current Tenant WebSocket connection');
      this.disconnect();
    }
    
    // Store new parameters
    this.currentTenantId = tenantId;
    this.currentUserId = userId;
  }

  // Manual reconnect method
  async manualReconnect() {
    //console.log('🔄 Manual reconnect triggered for Tenant WebSocket');
    
    this.reconnectAttempts = 0;
    this.isReconnecting = true;
    
    this.emit('connecting', { reason: 'manual_reconnect' });
    
    if (this.socket) {
      this.socket.close();
    }
    
    await this.handleReconnect();
  }
}

// Create singleton instance
const tenantWebSocketService = new TenantWebSocketService();

export default tenantWebSocketService;
